import { ProjectAppTypes, SearchField, useEnvProvider } from '@bechtel/client-common';
import {
  Box,
  Checkbox,
  FormControl,
  IconSize,
  MultiSelectCheckbox,
  Text,
  TextFieldAppearance,
  TextFieldSize,
} from '@bechtel/gds';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { filter, flatMap, includes, map, sortBy } from 'lodash';
import { FC, useDeferredValue, useState } from 'react';
import { useWorkPackage } from '~/detail/context';
import { useWorkPackagePermissions } from '~/hooks';
import { StatsContainerStyled } from './styled';
import { MilestoneName, WeldsProps } from './types';
import WeldItem from './weld-item';
import WeldStats from './weld-stats';

const Item = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
      {i18n._(item.name)}
    </Text>
  );
};

export const SelectedItem = ({ selectedItemCount }: any) => {
  return (
    <Text size="300" family="graphik" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
      {`${t`${selectedItemCount} Selected`}`}
    </Text>
  );
};

const Welds: FC<WeldsProps> = ({
  weldList,
  selectedWeldIds,
  setSelectedWeldIds,
  setSelectedWeld,
  selectedWeld,
  isFitupRequired,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { envConfig } = useEnvProvider();
  const deferredQuery = useDeferredValue(searchQuery);
  const [weldTypes, setWeldTypes] = useState<{ id: string; name: string }[]>([]);
  const filteredWelds = filter(weldList, (item) => includes(item?.tag?.toLowerCase(), deferredQuery?.toLowerCase()));
  const sortedWeldList = sortBy(filteredWelds, 'tag');
  const { assignedCrewMembers } = useWorkPackage();
  const { canCreateFitupRequest } = useWorkPackagePermissions(assignedCrewMembers);
  return (
    <>
      <StatsContainerStyled>
        <WeldStats
          label={t`Welds Fitup`}
          milestoneName={MilestoneName.Fitup}
          projectMilestoneTypes={weldList[0]?.type?.projectMilestoneTypes}
        />
        <WeldStats
          label={t`Welds Installation`}
          milestoneName={MilestoneName.Install}
          projectMilestoneTypes={weldList[0]?.type?.projectMilestoneTypes}
        />
      </StatsContainerStyled>

      <Box
        alignItems={{ sm: 'center' }}
        py={{ sm: '450' }}
        pl={{ sm: '450' }}
        pr={{ sm: '500' }}
        css={`
          border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
        `}
        data-testid="search-filter"
      >
        {canCreateFitupRequest && envConfig.appName === ProjectAppTypes.PF && (
          <Checkbox
            checked={selectedWeldIds.length === weldList?.length}
            indeterminate={selectedWeldIds.length > 0 && selectedWeldIds.length < weldList?.length}
            onChange={() => {
              if (selectedWeldIds.length >= 0 && selectedWeldIds.length !== weldList?.length) {
                setSelectedWeldIds(flatMap(weldList, 'id'));
              } else {
                setSelectedWeldIds([]);
              }
            }}
            readOnly
          />
        )}
        <Box pl={{ sm: '400' }} flexGrow={{ sm: 2 }}>
          <SearchField
            size={TextFieldSize.SMALL}
            iconSize={IconSize.SMALL}
            placeholder={t`Search`}
            type="search"
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            value={searchQuery}
          />
        </Box>
        <Box pl={{ sm: '400' }} flexShrink={{ sm: 2 }}>
          <FormControl style={{ flexShrink: 1 }} label="">
            <MultiSelectCheckbox
              items={weldTypes}
              itemComponent={Item}
              selectedComponent={SelectedItem}
              initialSelectedItems={[]}
              size={TextFieldSize.SMALL}
              showSelectAll={true}
              confirmSelection={true}
              appearance={TextFieldAppearance.NORMAL}
              displayKeyField="name"
              onSelectedItemsChange={(selectedWelds) => {
                setWeldTypes(selectedWelds);
              }}
              placeholder={t`All`}
              disabled={true}
            />
          </FormControl>
        </Box>
      </Box>
      <Box
        flexDirection={{ sm: 'column' }}
        flexGrow={{ sm: 1 }}
        style={{ overflowY: 'auto' }}
        data-testid="weld-list-container"
      >
        {sortedWeldList?.length > 0 ? (
          map(sortedWeldList, (weld) => {
            return (
              <WeldItem
                weld={weld}
                selectedWeld={selectedWeld}
                setSelectedWeld={setSelectedWeld}
                selectedWeldIds={selectedWeldIds}
                setSelectedWeldIds={setSelectedWeldIds}
                key={weld?.id}
                isFitupRequired={isFitupRequired}
              />
            );
          })
        ) : (
          <Box
            flexDirection={{ sm: 'column' }}
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            p={{ sm: '600' }}
            data-testid="no-welds"
          >
            <Text size="500" weight="600" color="grey.800">
              <Trans> No welds found.</Trans>
            </Text>
          </Box>
        )}
      </Box>
    </>
  );
};

export default Welds;
